import React from "react";
import { Organization } from "../../common/models";
import { BUSINESS_SURVEY_NAME, COLLECTION_FOR_OTHER_BUSINESSES_SLUG } from "./businessSurvey";
import { CONSUMER_INCENTIVES_SURVEY_NAME } from "./consumerCollectionIncentivesSurvey";
import { EEA_PHYSICAL_PRESENCE_SLUG, EEA_UK_SURVEY_NAME } from "./eeaUkSurvey";
import { ParsedSurveyAnswers } from "../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../pages/get-compliant/data-recipients/shared";
import { HR_SURVEY_NAME } from "./hrSurvey";

export type SURVEY_NAME =
  | typeof BUSINESS_SURVEY_NAME
  | typeof CONSUMER_INCENTIVES_SURVEY_NAME
  | typeof EEA_UK_SURVEY_NAME
  | typeof HR_SURVEY_NAME
  | typeof SELLING_AND_SHARING_SURVEY_NAME;

export interface OrgSettingQuestion {
  slug: string;
  source: SURVEY_NAME;
  label: string;
  question: string;
  help?: React.ReactNode;
  visibility?: (answers: ParsedSurveyAnswers) => boolean;
}

export const CONSUMER_FINANCIAL_INCENTIVES_QUESTION: OrgSettingQuestion = {
  slug: "offer-consumer-incentives",
  source: CONSUMER_INCENTIVES_SURVEY_NAME,
  label: "Offers financial incentives for collecting personal data",
  question:
    "Does your business offer financial incentives to consumers for sharing their personal information?",
  help: (
    <p>
      Answer "yes" if your business offers a loyalty or reward program, premium features, or a
      discount in exchange for a consumer providing their personal information. For example, 10% off
      a purchase with newsletter signup.
    </p>
  ),
};

export const CREATES_PROFILES_ABOUT_CONSUMERS_QUESTION: OrgSettingQuestion = {
  slug: "create-profiles-about-consumers",
  source: BUSINESS_SURVEY_NAME,
  label: "Uses automated profiling with legal or similar effects",
  question:
    "It’s exceedingly rare, but does your business use automated profiling to make decisions having legal or similarly significant effects on consumers?",
  help: (
    <>
      “Similarly significant effects” include decisions by your business to provide or deny a person
      money or credit, housing, insurance, education enrollment, criminal justice, employment
      opportunities, health care services, or access to basic necessities, such as food and water.
    </>
  ),
};

const ALL_SETTINGS_QUESTIONS: OrgSettingQuestion[] = [
  {
    slug: "business-service-provider",
    source: BUSINESS_SURVEY_NAME,
    label: "Is a service provider",
    question: 'Is your business a "service provider" under the CCPA?',
    help: (
      <>
        Answer “no” if you sell products to consumers, either directly or through a marketplace. An
        example of a service provider is a B2B company like TrueVault or Salesforce.{" "}
        <a
          href="https://knowledge.truevault.com/article/285-is-your-business-a-service-provider"
          target="_blank"
          rel="noreferrer"
        >
          Read more about "service providers."
        </a>
      </>
    ),
  },
  {
    slug: "business-physical-location",
    source: BUSINESS_SURVEY_NAME,
    label: "Has a physical presence in California",
    question:
      "Does your business have a physical presence in California where it collects information from consumers, like a storefront or office?",
    help: "If you have a physical presence in California but you only collect personal data from employees (e.g., an office building) and do not collect data directly from your B2C (or B2B if applicable) customers at that physical location, answer “No.”",
  },
  {
    slug: "business-products-aimed-at-kids-under-18",
    source: BUSINESS_SURVEY_NAME,
    label: "Offers products/services aimed at minors under 18",
    question:
      "Does your business knowingly collect the personal data of minors under the age of 18?",
    help: 'Answer "Yes" if your products, services, and/or website are aimed at minors under 18 or if you have actual knowledge that specific consumers are under 18 (for example, if your service systematically collects users\' birthdays).',
  },
  {
    slug: "business-products-aimed-at-kids-under-18-direct-collection",
    source: BUSINESS_SURVEY_NAME,
    label: "Collects data directly from minors under 18",
    question: "Does your business collect data directly from minors under 18?",
    help: 'Answer "No" if you collect data about minors under 18 only from their parents or legal guardians. Answer "Yes" if you ask for consumers\' age (e.g., birthday with year) and continue collecting data from consumers even when they indicate that they are under 18 years old. If you currently collect age but do not need to for any business purposes, you can discontinue the collection of age and answer "no" here.',
    visibility: (answers) => answers["business-products-aimed-at-kids-under-18"],
  },
  {
    slug: "business-products-aimed-at-kids",
    source: BUSINESS_SURVEY_NAME,
    label: "Offers products/services aimed at kids under 16",
    question:
      "Does your business knowingly collect the personal data of children under the age of 16?",
    help: 'Answer "Yes" if your products, services, and/or website are aimed at kids under 16 or if you have actual knowledge that specific consumers are under 16 (for example, if your service systematically collects users\' birthdays).',
  },
  {
    slug: "business-products-aimed-at-kids-direct-collection",
    source: BUSINESS_SURVEY_NAME,
    label: "Collects data directly from children under 16",
    question: "Does your business collect data directly from children under 16?",
    help: 'Answer "No" if you collect data about children under 16 only from their parents or legal guardians. Answer "Yes" if you ask for consumers\' age (e.g., birthday with year) and continue collecting data from consumers even when they indicate that they are under 16 years old. If you currently collect age but do not need to for any business purposes, you can discontinue the collection of age and answer "no" here.',
    visibility: (answers) => answers["business-products-aimed-at-kids"],
  },
  {
    slug: "business-products-aimed-at-kids-under-13",
    source: BUSINESS_SURVEY_NAME,
    label: "Offers products/services aimed at kids under 13",
    question: "Does your business knowingly collect personal data of children under the age of 13?",
    help: "Answer “Yes” if your business knowingly collects data from consumers under 13 years old.",
    visibility: (answers) => answers["business-products-aimed-at-kids"],
  },
  {
    slug: "business-products-aimed-at-kids-under-13-direct-collection",
    source: BUSINESS_SURVEY_NAME,
    label: "Collects data directly from children under 13",
    question: "Does your business collect data directly from children under 13?",
    help: 'Answer "No" if you collect data about children under 13 only from their parents or legal guardians. Answer "Yes" if you collect consumers\' age (e.g., birthday with year) and collect data from consumers even when they indicate that they are under 13 years old. If you currently collect age but do not need to for any business purposes, you can discontinue the collection of age and answer "no" here.',
    visibility: (answers) => answers["business-products-aimed-at-kids-under-13"],
  },
  {
    slug: "business-products-aimed-at-kids-under-13-parental-consent",
    source: BUSINESS_SURVEY_NAME,
    label: "Parental consent for kids under 13",
    question:
      "When you collect the data of children who you know are under the age of 13, do you obtain parental consent to collect their data before doing so?",
    help: "Several states require parental consent before collecting data of minors under 13. If you do not currently do this, we'll create an item on your to-do list for you to implement consent requirements.",
    visibility: (answers) => answers["business-products-aimed-at-kids-under-13"] == true,
  },
  CONSUMER_FINANCIAL_INCENTIVES_QUESTION,
  {
    slug: "business-has-mobile-apps",
    source: BUSINESS_SURVEY_NAME,
    label: "Has a mobile application",
    question: "Does your business have a mobile application?",
    help: (
      <p className="text-t1 mt-0">
        Answer “yes” if you have an app that consumers can download from the Apple App Store or
        Google Play store.
      </p>
    ),
  },
  {
    slug: "business-california-nonenglish",
    source: BUSINESS_SURVEY_NAME,
    label: "Communicates with consumers in other languages",
    question:
      "Does your business regularly communicate with consumers in any languages other than English?",
    help: (
      <p className="text-t1 mt-0">
        Answer “yes” if your business regularly sends marketing emails to consumers in other
        languages, or if its contracts, terms or agreements are ordinarily provided in other
        languages.
      </p>
    ),
  },
  {
    slug: COLLECTION_FOR_OTHER_BUSINESSES_SLUG,
    source: BUSINESS_SURVEY_NAME,
    label: "Collects (or purchases) personal information about unrelated individuals",
    question:
      "Does your business collect (or purchase) personal information about individuals who it does not have a direct relationship with?",
    help: (
      <p className="text-t1 mt-0">
        For example, answer 'yes' if your business receives contact information about website
        visitors from a{" "}
        <a
          href="https://www.truevault.com/learn/essentials/what-is-a-data-broker"
          target="_blank"
          rel="noreferrer"
        >
          data broker
        </a>{" "}
        like Retention.com or a tool like Wunderkind.
      </p>
    ),
  },
  {
    slug: "personal-data-large-scale",
    source: EEA_UK_SURVEY_NAME,
    label: "Performs “large scale” data processing of EEA/UK/CH residents",
    question: "Does your business process personal data of EEA/UK/CH residents on a “large scale”?",
    help: (
      <>
        <p className="text-t1 mt-0">
          Large scale processing is not common. Here are some examples where it would apply:
        </p>
        <ul>
          <li>
            Your business processes unaggregated personal data collected from monitoring a publicly
            accessible area, such as a park, airport or train station, on a large scale
          </li>
          <li>
            Your business routinely processes real-time geolocation data of customers across
            multiple EU countries for statistical purposes
          </li>
          <li>
            Your business is an insurance company, bank, search engine or internet service provider
          </li>
          <li>
            Your business processes the personal data of a significant portion of the population of
            the UK or any EU country (‘processing’ includes tracking online behavior)
          </li>
        </ul>
        <p>
          Click{" "}
          <a
            href="https://knowledge.truevault.com/article/226-large-scale-processing"
            target="_blank"
            rel="norefferer noopener noreferrer"
          >
            here
          </a>{" "}
          to read more about “large scale” data processing.
        </p>
      </>
    ),
  },
  {
    slug: "business-employees-co",
    source: HR_SURVEY_NAME,
    label: "Has employees or contractors in Colorado",
    question: "Does your business have employees or contractors in Colorado?",
    help: "Colorado has special requirements for businesses that collect biometric data, including such data about employees. We'll ask a few questions to see if this applies. If it does, we'll ask you to complete the required steps.",
  },
  {
    slug: "business-biometric-identifiers-co",
    source: HR_SURVEY_NAME,
    label: "Collects biometric identifiers in Colorado",
    question:
      'Does your business collect "biometric identifiers" about employees or contractors in Colorado?',
    help: "Biometric identifiers are data related to a person's biological, physical, or behavioral characteristics, if that data can be used to identify that person. For example, fingerprints, voiceprints, facial geometry, or physical patterns or characteristics that can uniquely identify someone. For example, using facial scans or fingerprints to clock employees in and out of work shifts or to gain access to work premises.",
    visibility: (answers) => answers["business-employees-co"] === true,
  },
  {
    slug: "employees-eea-uk",
    source: HR_SURVEY_NAME,
    label: "Has employees in the EEA/UK/CH",
    question: "Does your business have workers (employees or contractors) living in the EEA/UK/CH?",
  },
  {
    slug: EEA_PHYSICAL_PRESENCE_SLUG,
    source: EEA_UK_SURVEY_NAME,
    label: "Has physical presence in the EEA/UK/CH",
    question: "Does your business have a physical presence in the EEA, UK, or Switzerland?",
    help: (
      <div>
        <p className="text-t1 mt-0">
          A physical presence would include, for example, a retail location, a corporate office, or
          any official address where your business can receive correspondence and notices.
        </p>
      </div>
    ),
  },
  CREATES_PROFILES_ABOUT_CONSUMERS_QUESTION,
];

export const organizationSettingsQuestions = (org: Organization) => {
  return ALL_SETTINGS_QUESTIONS.filter(
    (q) => org.featureGdpr || (q.source !== EEA_UK_SURVEY_NAME && q.slug !== "employees-eea-uk"),
  );
};
